import React, { SVGProps } from 'react';
import { styled, Typography, TypographyProps } from '@mui/material';
import { LabelProps } from 'recharts';
import { CartesianViewBox, Coordinate, PolarViewBox } from 'recharts/types/util/types';

type CustomChartLabelProps = TypographyProps & { labelColor?: string; active?: boolean };

export const CUSTOM_CHART_LABEL_MAX_WIDTH = 62;
const LABEL_HEIGHT = 20;

const ACTIVE_CUSTOM_CHART_LABEL_CLASS = 'CustomChartLabel__active';

const StyledTypography = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1.25),
  paddingRight: theme.spacing(0.75),
  paddingTop: theme.spacing(0.125),
  paddingBottom: theme.spacing(0.125),
  borderRadius: theme.spacing(2),
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--color-content-base-secondary)',
  border: 0.5,
  borderColor: 'var(--color-border-base-secondary)',
  borderStyle: 'solid',
  backgroundColor: 'var(--color-surface-elevated-20)',
  height: LABEL_HEIGHT,
  maxWidth: CUSTOM_CHART_LABEL_MAX_WIDTH,
  position: 'relative',
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  transition: theme.transitions.create('border-color', { duration: theme.transitions.duration.short }),
  backdropFilter: 'blur(3px)',
  userSelect: 'none',

  [`&:hover,
    &.${ACTIVE_CUSTOM_CHART_LABEL_CLASS}`]: { borderColor: 'var(--color-border-base-tertiary)' },

  '&:before': {
    content: '" "',
    position: 'absolute',
    borderRadius: '4px',
    top: '3px',
    left: '4px',
    width: '4px',
    height: '12px',
    background: `var(--label-color, var(--dataviz-color-categorical-primary-chart01))`,
  },
}));

export const CustomChartLabel = ({ labelColor, ref, active, className, style, ...props }: CustomChartLabelProps) => (
  <StyledTypography
    ref={ref}
    component="span"
    variant="body1"
    noWrap
    className={`${className} ${active ? ACTIVE_CUSTOM_CHART_LABEL_CLASS : ''}`.trim()}
    style={{
      // @ts-expect-error: CSS variable
      '--label-color': labelColor,
      ...style,
    }}
    {...props}
  />
);

const RADIAN = Math.PI / 180;

const isPolar = (viewBox: CartesianViewBox | PolarViewBox): viewBox is PolarViewBox =>
  'cx' in viewBox && typeof viewBox.cx === 'number' && !Number.isNaN(viewBox.cx);

const polarToCartesian = (cx: number, cy: number, radius: number, angle: number): Coordinate => ({
  x: cx + Math.cos(-RADIAN * angle) * radius,
  y: cy + Math.sin(-RADIAN * angle) * radius,
});

const getAttrsOfCartesianLabel = (props: CustomLabelContentTypeProps) => {
  const { viewBox = {}, parentViewBox = {}, offset = 0, position } = props;
  const { x = 0, y = 0, width = 0, height = 0 } = viewBox as CartesianViewBox;

  const verticalSign = height >= 0 ? 1 : -1;
  const verticalOffset = verticalSign * offset;
  const verticalEnd = verticalSign > 0 ? 'end' : 'start';
  const verticalStart = verticalSign > 0 ? 'start' : 'end';

  const horizontalSign = width >= 0 ? 1 : -1;
  const horizontalOffset = horizontalSign * offset;
  const horizontalEnd = horizontalSign > 0 ? 'end' : 'start';
  const horizontalStart = horizontalSign > 0 ? 'start' : 'end';

  if (position === 'top') {
    const attrs = {
      x: x + width / 2,
      y: y - verticalSign * offset,
      textAnchor: 'middle',
      verticalAnchor: verticalEnd,
    };

    return {
      ...attrs,
      ...(parentViewBox
        ? {
            height: Math.max(y - ((parentViewBox as CartesianViewBox).y || 0), 0),
            width,
          }
        : {}),
    };
  }

  if (position === 'bottom') {
    const attrs = {
      x: x + width / 2,
      y: y + height + verticalOffset,
      textAnchor: 'middle',
      verticalAnchor: verticalStart,
    };

    return {
      ...attrs,
      ...(parentViewBox
        ? {
            height: Math.max(
              ((parentViewBox as CartesianViewBox).y || 0) + ((parentViewBox as CartesianViewBox).height || 0) - (y + height),
              0
            ),
            width,
          }
        : {}),
    };
  }

  if (position === 'left') {
    const attrs = {
      x: x - horizontalOffset,
      y: y + height / 2,
      textAnchor: horizontalEnd,
      verticalAnchor: 'middle',
    };

    return {
      ...attrs,
      ...(parentViewBox
        ? {
            width: Math.max(attrs.x - ((parentViewBox as CartesianViewBox).x || 0), 0),
            height,
          }
        : {}),
    };
  }

  if (position === 'right') {
    const attrs = {
      x: x + width + horizontalOffset,
      y: y + height / 2,
      textAnchor: horizontalStart,
      verticalAnchor: 'middle',
    };
    return {
      ...attrs,
      ...(parentViewBox
        ? {
            width: Math.max(((parentViewBox as CartesianViewBox).x || 0) + ((parentViewBox as CartesianViewBox).width || 0) - attrs.x, 0),
            height,
          }
        : {}),
    };
  }

  const sizeAttrs = parentViewBox ? { width, height } : {};

  if (position === 'insideLeft') {
    return {
      x: x + horizontalOffset,
      y: y + height / 2,
      textAnchor: horizontalStart,
      verticalAnchor: 'middle',
      ...sizeAttrs,
    };
  }

  if (position === 'insideRight') {
    return {
      x: x + width - horizontalOffset,
      y: y + height / 2,
      textAnchor: horizontalEnd,
      verticalAnchor: 'middle',
      ...sizeAttrs,
    };
  }

  if (position === 'insideTop') {
    return {
      x: x + width / 2,
      y: y + verticalOffset,
      textAnchor: 'middle',
      verticalAnchor: verticalStart,
      ...sizeAttrs,
    };
  }

  if (position === 'insideBottom') {
    return {
      x: x + width / 2,
      y: y + height - verticalOffset,
      textAnchor: 'middle',
      verticalAnchor: verticalEnd,
      ...sizeAttrs,
    };
  }

  if (position === 'insideTopLeft') {
    return {
      x: x + horizontalOffset,
      y: y + verticalOffset,
      textAnchor: horizontalStart,
      verticalAnchor: verticalStart,
      ...sizeAttrs,
    };
  }

  if (position === 'insideTopRight') {
    return {
      x: x + width - horizontalOffset,
      y: y + verticalOffset,
      textAnchor: horizontalEnd,
      verticalAnchor: verticalStart,
      ...sizeAttrs,
    };
  }

  if (position === 'outsideTopRight') {
    return {
      x: x + width + horizontalOffset,
      y,
      textAnchor: horizontalEnd,
      verticalAnchor: verticalStart,
      ...sizeAttrs,
    };
  }

  if (position === 'outsideBottomRight') {
    return {
      x: x + width,
      y: y + height + verticalOffset,
      textAnchor: horizontalEnd,
      verticalAnchor: verticalEnd,
      ...sizeAttrs,
    };
  }

  if (position === 'insideBottomLeft') {
    return {
      x: x + horizontalOffset,
      y: y + height - verticalOffset,
      textAnchor: horizontalStart,
      verticalAnchor: verticalEnd,
      ...sizeAttrs,
    };
  }

  if (position === 'insideBottomRight') {
    return {
      x: x + width - horizontalOffset,
      y: y + height - verticalOffset,
      textAnchor: horizontalEnd,
      verticalAnchor: verticalEnd,
      ...sizeAttrs,
    };
  }

  return {
    x: x + width / 2,
    y: y + height / 2,
    textAnchor: 'middle',
    verticalAnchor: 'middle',
    ...sizeAttrs,
  };
};

const getAttrsOfPolarLabel = (props: CustomLabelContentTypeProps) => {
  const { viewBox, offset = 0, position } = props;
  const { cx = 0, cy = 0, innerRadius = 0, outerRadius = 0, startAngle = 0, endAngle = 0 } = viewBox as PolarViewBox;
  const midAngle = (startAngle + endAngle) / 2;

  if (position === 'outside') {
    const { x, y } = polarToCartesian(cx, cy, outerRadius + offset, midAngle);

    return {
      x,
      y,
      textAnchor: x >= cx ? 'start' : 'end',
      verticalAnchor: 'middle',
    };
  }

  if (position === 'center') {
    return {
      x: cx,
      y: cy,
      textAnchor: 'middle',
      verticalAnchor: 'middle',
    };
  }

  if (position === 'centerTop') {
    return {
      x: cx,
      y: cy,
      textAnchor: 'middle',
      verticalAnchor: 'start',
    };
  }

  if (position === 'centerBottom') {
    return {
      x: cx,
      y: cy,
      textAnchor: 'middle',
      verticalAnchor: 'end',
    };
  }

  const r = (innerRadius + outerRadius) / 2;
  const { x, y } = polarToCartesian(cx, cy, r, midAngle);

  return {
    x,
    y,
    textAnchor: 'middle',
    verticalAnchor: 'middle',
  };
};

const rechartsCustomChartLabelClassNames = {
  barChartHorizontalTop: '.recharts-wrapper.horizontal .recharts-bar .chart-label-wrapper.top',
  barChartHorizontalRight: '.recharts-wrapper.horizontal .recharts-bar .chart-label-wrapper.right',
  barChartVerticalTop: '.recharts-wrapper.vertical .recharts-bar .chart-label-wrapper.top',
  barChartVerticalRight: '.recharts-wrapper.vertical .recharts-bar .chart-label-wrapper.right',
  barChartVerticalOutsideBottomRight: '.recharts-wrapper.vertical .recharts-bar .chart-label-wrapper.outsideBottomRight',
  pieChartVerticalOutside: '.recharts-wrapper .recharts-pie .chart-label-wrapper.outside',
  lineChartTop: '.recharts-wrapper .recharts-line .chart-label-wrapper.top',
} as const;

export type CustomLabelContentTypeProps = Omit<SVGProps<SVGTextElement>, 'viewBox'> &
  Omit<LabelProps, 'position'> & { position?: LabelProps['position'] | 'outsideTopRight' | 'outsideBottomRight' };

export const formatNumberCompact = (value?: number | string | null) =>
  (typeof value === 'string' && value) || typeof value === 'number'
    ? new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1,
      }).format(Number(value))
    : null;

const CustomLabelForeignObject = (props: CustomLabelContentTypeProps) => {
  const { viewBox, position, children, value } = props;
  const wrapperClassname = `chart-label-wrapper ${position}`;
  const positionAttrs = (isPolar(viewBox as PolarViewBox) ? getAttrsOfPolarLabel(props) : getAttrsOfCartesianLabel(props)) as Partial<
    SVGProps<SVGForeignObjectElement>
  >;

  if (value === undefined || value === null || Number(value) === 0) {
    return null;
  }

  return (
    <foreignObject
      style={{ position: 'relative', overflow: 'visible' }}
      className={wrapperClassname}
      {...positionAttrs}
      width={1}
      height={1}>
      {children}
    </foreignObject>
  );
};

const createActiveBarLabelSelector = (name?: string) =>
  `.recharts-surface:has(&) .recharts-bar:has(.recharts-rectangle.active-bar-cell[data-bar-active-cell-name="${name}"]) &`;
const createActiveLineLabelSelector = (index?: number) =>
  `.recharts-surface:has(&):has(.recharts-active-dot .recharts-dot[data-line-active-dot-index="${index}"]) &`;

export const BarLineLabel = (props: CustomLabelContentTypeProps) => {
  const { value, fill, index, name } = props;
  const displayValue = formatNumberCompact(value);
  const isActive = !!('active' in props && props.active);
  return (
    <CustomLabelForeignObject {...props}>
      <CustomChartLabel
        sx={{
          position: 'absolute',
          [`${rechartsCustomChartLabelClassNames.barChartHorizontalTop} &`]: {
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
          },
          [`${rechartsCustomChartLabelClassNames.barChartVerticalRight} &`]: {
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
          },
          [`${rechartsCustomChartLabelClassNames.barChartVerticalOutsideBottomRight} &`]: {
            right: 0,
          },
          [`${rechartsCustomChartLabelClassNames.lineChartTop} &`]: {
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
          },

          [`${createActiveBarLabelSelector(name)},
            ${createActiveLineLabelSelector(index)}`]: { borderColor: 'var(--color-border-base-tertiary)' },
        }}
        labelColor={fill}
        active={isActive}>
        {displayValue}
      </CustomChartLabel>
    </CustomLabelForeignObject>
  );
};

const createActivePolarLabelSelector = (index?: number) =>
  `.recharts-surface:has(&) .recharts-pie:has(.recharts-sector.active-pie-cell[data-pie-active-cell-index="${index}"]) &`;

export const PolarLabel = (props: CustomLabelContentTypeProps) => {
  const { value, fill, viewBox, offset = 0 } = props;
  const positionAttrs = getAttrsOfPolarLabel(props);
  const { cx = 0, cy = 0 } = viewBox as PolarViewBox;
  const labelX = positionAttrs.x ?? 0;
  const labelY = positionAttrs.y ?? 0;
  const dx = labelX - cx;
  const dy = labelY - cy;
  const angle = Math.atan2(dy, dx);
  const offsetX = Math.cos(angle) * (CUSTOM_CHART_LABEL_MAX_WIDTH / 2 + offset);
  const offsetY = Math.sin(angle) * (LABEL_HEIGHT / 2 + offset);
  const displayValue = formatNumberCompact(value);

  return (
    <CustomLabelForeignObject {...props}>
      <CustomChartLabel
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: `translate(-50%, -50%) translate(${offsetX}px, ${offsetY}px)`,

          [`${createActivePolarLabelSelector(props.index)}`]: { borderColor: 'var(--color-border-base-tertiary)' },
        }}
        labelColor={fill}>
        {displayValue}
      </CustomChartLabel>
    </CustomLabelForeignObject>
  );
};
